import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { PasswordHashResult } from '@/services/abstract/poc/crypto.service.interface';
import { calculateHashThunk } from '@/store/thunks/poc/calculateHashThunk';
import { registerPasswordThunk } from '@/store/thunks/auth/registerPasswordThunk';
import { recoverPasswordThunk } from '@/store/thunks/auth/recoverPasswordThunk';
import { extractThunkError } from '@/store/utils';

export interface PocAuthState {
  password: string;
  email: string;
  hashResult: PasswordHashResult | null;
  hashStatus: 'idle' | 'calculating' | 'ready' | 'error';
  errorMessage: string | null;
  derivedKey: string | null;
  kyberKeyPair: {
    publicKey: Uint8Array | null;
    privateKey: Uint8Array | null;
  };
  encapsulatedKey: {
    sharedSecret: Uint8Array | null;
    ciphertext: Uint8Array | null;
  } | null;
  privateKeyHash: string | null;
}

const initialState: PocAuthState = {
  password: 'oreo',
  hashResult: null,
  hashStatus: 'idle',
  errorMessage: null,
  email: 'adhochstetter@gmail.com',
  kyberKeyPair: {
    publicKey: null,
    privateKey: null,
  },
  encapsulatedKey: null,
  privateKeyHash: null,
  derivedKey: null,
};

const pocAuthSlice = createSlice({
  name: 'pocAuth',
  initialState,
  reducers: {
    setPassword: (state, action: PayloadAction<string>) => {
      state.password = action.payload;

      if (state.hashResult !== null) {
        state.hashStatus = 'idle';
        state.hashResult = null;
      }
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;

      if (state.hashResult !== null) {
        state.hashStatus = 'idle';
      }
    },
    clearPassword: (state) => {
      state.password = '';
      state.hashResult = null;
      state.hashStatus = 'idle';
      state.errorMessage = null;
    },
    setDerivedKey: (state, action: PayloadAction<string>) => {
      state.derivedKey = action.payload;
    },
    clearKeys: (state) => {
      state.derivedKey = null;
      state.encapsulatedKey = null;
      state.kyberKeyPair = {
        publicKey: null,
        privateKey: null,
      };
      state.privateKeyHash = null;
      state.errorMessage = null;
    },
    setKeyPair: (
      state,
      action: PayloadAction<{
        publicKey: Uint8Array;
        privateKey: Uint8Array;
      }>
    ) => {
      state.kyberKeyPair = action.payload;
    },
    setPrivateKeyHash: (state, action: PayloadAction<string>) => {
      state.privateKeyHash = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(calculateHashThunk.pending, (state) => {
        state.hashStatus = 'calculating';
        state.errorMessage = null;
      })
      .addCase(calculateHashThunk.fulfilled, (state, action) => {
        state.hashResult = action.payload;
        state.hashStatus = 'ready';
      })
      .addCase(calculateHashThunk.rejected, (state, action) => {
        const { message } = extractThunkError(action.payload);
        state.hashStatus = 'error';
        state.errorMessage = message ?? action.error.message ?? 'Unknown error';
      })
      .addCase(registerPasswordThunk.fulfilled, (state, action) => {
        state.derivedKey = action.payload.derivedKey;
        state.kyberKeyPair = {
          publicKey: action.payload.keyPair.publicKey,
          privateKey: action.payload.keyPair.privateKey,
        };
        state.privateKeyHash = action.payload.encryptedPrivateKey;
      })
      .addCase(recoverPasswordThunk.fulfilled, (state, action) => {
        state.derivedKey = action.payload.derivedKey;
        state.kyberKeyPair = {
          publicKey: action.payload.keyPair.publicKey,
          privateKey: action.payload.keyPair.privateKey,
        };
        state.privateKeyHash = action.payload.encryptedPrivateKey;
      });
  },
});

export const {
  setPassword,
  setEmail,
  clearPassword,
  setDerivedKey,
  clearKeys,
  setKeyPair,
  setPrivateKeyHash,
} = pocAuthSlice.actions;

export default pocAuthSlice.reducer;
